/**
 * utils/apiClient.js — Centralised HTTP client with JWT auth
 * ============================================================
 * Exports a singleton `apiClient` instance used by every page component.
 * All API calls go through this file so auth headers, token refresh, and
 * error handling are handled in one place.
 *
 * Architecture overview
 * ----------------------
 * TokenManager  — static class that reads/writes the JWT from localStorage.
 *                 Decodes the JWT payload to check expiry without a round-trip.
 *
 * ApiClient     — wraps an Axios instance.
 *                 Request interceptor:  attaches Bearer token to every request.
 *                 Response interceptor: on 401, silently refreshes the token
 *                 (POST /session/refresh) and retries the original request.
 *                 While a refresh is in-flight, subsequent 401s are queued
 *                 rather than triggering parallel refresh calls.
 *
 * Auth failure flow
 * -----------------
 * If refresh also fails (refresh token expired / revoked):
 *   1. localStorage is cleared.
 *   2. A custom DOM event 'auth:logout' is dispatched.
 *   3. App.js listener resets usr state → ProtectedRoute redirects to /.
 *
 * Environment variables required
 * --------------------------------
 * REACT_APP_API_ENDPOINT  — base URL of the backend API (no trailing slash).
 *                           e.g. http://localhost:8000
 *
 * Public vs protected endpoints
 * --------------------------------
 * Public (no token required):
 *   login, searchPosts, readPost, getMonthlyStats, getLandmarks
 *
 * Protected (Bearer token required, handled automatically):
 *   getUsersList, submitPost, getNewsfeed, fetchPost, updatePost,
 *   updateUser, recordReadReceipt, factCheck, sessionurl, removeurl,
 *   getUserDist
 *
 * Deprecated / removed endpoints
 * --------------------------------
 * getStats      — POST /stats       DEPRECATED: endpoint removed from backend.
 *                 Replaced by getMonthlyStats (POST /monthly_stats).
 *
 * getBoundaries — POST /boundaries  DEPRECATED: endpoint removed from backend.
 *                 GeoJSON boundaries are now returned as part of monthly_stats
 *                 response (B1 panel).
 */

import axios from 'axios';

// Send cookies on every cross-origin request (required for cookie-based auth).
axios.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// TokenManager — localStorage JWT helpers
// ---------------------------------------------------------------------------

class TokenManager {
  /**
   * Read the JWT from the `usr` object stored in localStorage.
   * Returns null if no user is logged in or the stored data is malformed.
   */
  static getToken() {
    const usr = localStorage.getItem('usr');
    if (usr) {
      const userData = JSON.parse(usr);
      return userData.token;
    }
    return null;
  }

  /**
   * Update the token field inside the `usr` object in localStorage,
   * preserving all other user fields (email, role, addr, etc.).
   */
  static setToken(token) {
    const usr = localStorage.getItem('usr');
    if (usr) {
      const userData = JSON.parse(usr);
      userData.token = token;
      localStorage.setItem('usr', JSON.stringify(userData));
    }
  }

  /** Remove the entire `usr` entry from localStorage (full logout). */
  static removeToken() {
    localStorage.removeItem('usr');
  }

  /** Update only the adc_ts field inside the stored `usr` object. */
  static setAdcTs(adc_ts) {
    const usr = localStorage.getItem('usr');
    if (usr) {
      const userData = JSON.parse(usr);
      userData.adc_ts = adc_ts;
      localStorage.setItem('usr', JSON.stringify(userData));
    }
  }

  /** Return the full parsed user object, or null if not logged in. */
  static getUserData() {
    const usr = localStorage.getItem('usr');
    return usr ? JSON.parse(usr) : null;
  }

  /** Overwrite the entire `usr` object in localStorage. */
  static setUserData(userData) {
    localStorage.setItem('usr', JSON.stringify(userData));
  }

  /**
   * Decode the JWT payload (base64url) and check whether `exp` is in the past.
   * Returns true if the token is missing, malformed, or expired.
   */
  static isTokenExpired(token) {
    if (!token) return true;

    try {
      // JWT structure: header.payload.signature (all base64url-encoded)
      const payload = JSON.parse(atob(token.split('.')[1]));
      // `exp` is in seconds; Date.now() is in milliseconds
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;  // treat unparseable token as expired
    }
  }
}

// ---------------------------------------------------------------------------
// ApiClient — Axios wrapper with interceptors
// ---------------------------------------------------------------------------

class ApiClient {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_ENDPOINT;

    // Flags used by the 401 response interceptor to prevent parallel refreshes.
    this.isRefreshing = false;
    this.failedQueue = [];  // pending requests waiting for the new token

    // Create a dedicated Axios instance so interceptors don't leak to global axios.
    this.client = axios.create({
      baseURL: this.baseURL,
      // withCredentials: true, // Required to send/receive cookies across origins
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // -----------------------------------------------------------------------
    // Request interceptor — attach Bearer token to every outgoing request.
    // Runs before the request is sent; no token means the header is omitted
    // (public endpoints do not require one).
    // -----------------------------------------------------------------------
    this.client.interceptors.request.use(
      (config) => {
        const token = TokenManager.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // -----------------------------------------------------------------------
    // Response interceptor — silently refresh the token on 401 and retry.
    //
    // Flow for the first 401:
    //   1. Mark the request as retried (_retry flag) to prevent infinite loops.
    //   2. Call refreshToken() → POST /session/refresh with current token.
    //   3. Store the new token via TokenManager.setToken().
    //   4. Drain the failedQueue (resolve each queued promise with new token).
    //   5. Retry the original request with the new Authorization header.
    //
    // Flow for concurrent 401s while a refresh is already in-flight:
    //   - Additional requests are pushed to failedQueue instead of triggering
    //     a second refresh.  They are resolved/rejected together when the
    //     in-flight refresh completes.
    // -----------------------------------------------------------------------
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // A refresh is already in-flight — queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.refreshToken();
            this.processQueue(null, newToken);  // resolve all queued requests

            // Retry the original request with the fresh token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError, null);  // reject all queued requests
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Refresh the session token.
   * Calls POST /session/refresh with the current token in the Authorization
   * header.  On success, stores the new token via TokenManager and returns it.
   * Throws on failure so the 401 interceptor can handle auth cleanup.
   */
  async refreshToken() {
    const currentToken = TokenManager.getToken();

    if (!currentToken) {
      throw new Error('No token available for refresh');
    }

    try {
      const response = await axios.post(
        `${this.baseURL}/session/refresh`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`
          }
        }
      );

      const { token, adc_ts } = response.data;
      TokenManager.setToken(token);
      if (adc_ts) TokenManager.setAdcTs(adc_ts);

      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Resolve or reject all requests that were queued while a token refresh
   * was in-flight.  Called once the refresh attempt settles.
   *
   * @param {Error|null} error  — pass an Error to reject all queued requests.
   * @param {string|null} token — pass the new token to resolve all queued requests.
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Handle an unrecoverable auth failure (refresh also returned 401).
   * Clears localStorage, dispatches 'auth:logout' for App.js to catch,
   * and redirects to /search if not already there.
   */
  handleAuthFailure() {
    TokenManager.removeToken();

    // App.js listens for this event and resets the usr context to the empty state,
    // which causes ProtectedRoute to redirect away from protected pages.
    window.dispatchEvent(new CustomEvent('auth:logout'));

    if (window.location.pathname !== '/search') {
      window.location.href = '/search';
    }
  }

  // ---------------------------------------------------------------------------
  // Generic HTTP wrappers — not called directly by page components but expose
  // the full Axios interface for one-off or future use.
  // ---------------------------------------------------------------------------

  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  async put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  // ---------------------------------------------------------------------------
  // Public API methods — no authentication required
  // These call axios directly (not this.client) so no token is sent,
  // even if the user happens to be logged in.
  // ---------------------------------------------------------------------------

  /**
   * Exchange a Google OAuth credential for a backend JWT session token.
   * The Google `credential` (JWT) is passed in the Authorization header;
   * the user's current device coordinates are included in the request body.
   *
   * @param {string} credentials — Google JWT returned by GoogleLogin onSuccess.
   * @param {{ lon: number, lat: number }} location — device coordinates.
   * @returns {Promise} Axios response; response.data contains the full usr object.
   */
  async login(credentials, location) {
    const loc = (location.lon != null && location.lat != null)
      ? [location.lon, location.lat]
      : null;
    return axios.post(`${this.baseURL}/session/login`,
      { loc },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials}`   // Google JWT, not session token
        }
      }
    );
  }

  /**
   * Find fact-checked posts similar to the given text using Elasticsearch
   * more-like-this.  Used by Search.js to surface existing verdicts.
   *
   * @param {string} msg — free-text claim to search for.
   */
  async searchPosts(msg) {
    return axios.post(`${this.baseURL}/search`, { 'msg': msg }, {
      'Content-Type': 'application/json',
    });
  }

  /**
   * Fetch the full detail of a single post by its address.
   * Used by Search.js to show the fact-check verdict in a modal.
   * NOTE: This is the *public* read endpoint — no auth required.
   *       Use fetchPost() for the authenticated version used in Newsfeed.
   *
   * @param {string} addr — '<index>/<id>' post address.
   */
  async readPost(addr) {
    return axios.post(`${this.baseURL}/read`, {
      'addr': addr
    }, {
      'Content-Type': 'application/json',
    });
  }

  /**
   * Fetch statistics and chart panel data for a specific month and region.
   * Powers the entire Home.js dashboard (P1–P4, B1, H1.1, H1.2).
   *
   * @param {number}   year    — 4-digit year e.g. 2025.
   * @param {number}   month   — 1-based month number e.g. 7 for July.
   * @param {string[]} regions — region path prefixes to filter by.
   * @param {string}   metric  — 'Users' or 'Posts' (controls which heatmap is returned).
   */
  async getMonthlyStats(year, month, regions, metric) {
    return axios.post(`${this.baseURL}/monthly_stats`, {
      'regions': regions,
      'year': year,
      'month': month,
      'metric': metric
    }, {
      'Content-Type': 'application/json',
    });
  }

  /**
   * Fetch the full GIS region hierarchy tree (country → state → district → taluk).
   * Used by Home.js to populate the TreeSelect region picker.
   * Returns the tree in Ant Design TreeSelect data format.
   */
  async getLandmarks() {
    return axios.post(`${this.baseURL}/landmarks`, {}, {
      'Content-Type': 'application/json',
    });
  }

  // DEPRECATED — POST /stats endpoint has been removed from the backend.
  // This method is superseded by getMonthlyStats() above.
  // async getStats(from, to) {
  //   return axios.post(`${this.baseURL}/stats`, {
  //     'from_dt': from,
  //     'to_dt': to
  //   }, {
  //     'Content-Type': 'application/json',
  //   });
  // }

  // DEPRECATED — POST /boundaries endpoint has been removed from the backend.
  // GeoJSON boundary data is now returned as the B1 panel inside monthly_stats.
  // async getBoundaries(regions, metric, year, month) {
  //   return axios.post(`${this.baseURL}/boundaries`, {
  //     'regions': regions,
  //     'metric': metric,
  //     'year': year,
  //     'month': month
  //   }, {
  //     'Content-Type': 'application/json',
  //   });
  // }

  // ---------------------------------------------------------------------------
  // Protected API methods — require a valid Bearer token.
  // Token is attached automatically by the request interceptor.
  // ---------------------------------------------------------------------------

  /**
   * Search for users by email prefix.  ADMIN-only page (Userslist.js).
   *
   * @param {string} query — partial email string; '' returns all users.
   */
  async getUsersList(query = '') {
    return this.post('/session/usrslist', { usr_qry: query });
  }

  /**
   * Submit a new claim for fact-checking.
   * The backend deduplicates by message text and applies ADC cooldown logic.
   * Response includes `adc_ts` (next allowed submit time) if rate-limited.
   *
   * @param {string} msg      — full text of the claim.
   * @param {[number, number]} loc — [longitude, latitude] of the submission.
   * @param {Array} files     — media attached at submission time [{url, thumbUrl}].
   */
  async submitPost(msg, loc, files = []) {
    return this.post('/session/submit', { 'msg': msg, 'loc': loc, 'files': files });
  }

  /**
   * Fetch the news feed for a user — fact-checked posts matching the given
   * label, date range, and state.
   * Each result includes a `viewed` flag indicating whether this user has
   * already recorded a read receipt for the post.
   *
   * @param {string} usr_addr  — backend address of the logged-in user.
   * @param {string} from      — start date 'YYYY-MM-DD'.
   * @param {string} to        — end date   'YYYY-MM-DD'.
   * @param {string} newstype  — label to filter: 'FAKE'|'REAL'|'SPAM'|'BLANK'.
   * @param {string} state     — GIS state name to restrict results.
   */
  async getNewsfeed(usr_addr, from, to, newstype, state) {
    return this.post('/session/newsfeed', {
      'lbl': newstype.toUpperCase(),
      'from_dt': from,
      'to_dt': to,
      'usr_addr': usr_addr,
      'state': state
    });
  }

  /**
   * Fetch the full detail of a post for the authenticated user.
   * Used in Newsfeed.js when opening the fact-check modal.
   * Unlike readPost(), this endpoint enforces auth and may return
   * additional fields (e.g. files) not exposed on the public endpoint.
   *
   * @param {string} addr — '<index>/<id>' post address.
   */
  async fetchPost(addr) {
    return this.post('/session/fetch_post', { 'addr': addr });
  }

  /**
   * Partial-update a post document (verdict, explanation, files).
   * Only FACT_CHECKER role is authorised by the backend.
   *
   * @param {string} addr    — '<index>/<id>' post address.
   * @param {object} updates — fields to update: { lbl, exp, files }.
   */
  async updatePost(addr, updates) {
    return this.post('/session/update_post', { 'addr': addr, 'pst_doc': updates });
  }

  /**
   * Partial-update a user document (role).
   * Only ADMIN role is authorised by the backend.
   *
   * @param {string} addr    — '<index>/<id>' user address.
   * @param {object} updates — fields to update: { role }.
   */
  async updateUser(addr, updates) {
    return this.post('/session/update_user', { 'addr': addr, 'usr_doc': updates });
  }

  /**
   * recordStats — single endpoint for all user-post interaction signals.
   *
   * Pass only the fields relevant to the interaction; omitted fields are
   * not sent and the backend will not touch them.
   *
   * View event  : { viewed: true, loc }
   * Affinity    : { affinity, affinity_fc_ts }
   * Severity    : { severity }
   */
  async recordStats(usr_addr, msg_addr, lbl, role, {
    viewed         = false,
    loc            = null,
    affinity       = null,
    affinity_fc_ts = null,
    severity       = null,
  } = {}) {
    const payload = { 'usr_addr': usr_addr, 'msg_addr': msg_addr, 'lbl': lbl, 'role': role };
    if (viewed)                  { payload['viewed'] = true;                   payload['loc'] = loc; }
    if (affinity       !== null) { payload['affinity']       = affinity;       payload['affinity_fc_ts'] = affinity_fc_ts; }
    if (severity       !== null) { payload['severity']       = severity; }
    return this.post('/session/record_stats', payload);
  }

  /**
   * Trigger AI-assisted fact-check generation for a post.
   * Calls the Gemini API on the backend and returns a pre-filled markdown
   * explanation (`gen_ai_fill`) that the fact-checker can edit before saving.
   * Only available to FACT_CHECKER role.
   *
   * @param {string} addr — '<index>/<id>' post address.
   */
  async factCheck(addr) {
    return this.post('/session/fact_check', { 'addr': addr });
  }

  /**
   * Request a Google Cloud Storage pre-signed upload URL for a media file.
   * The client uploads directly to GCS using the returned `uploadUrl`
   * (PUT request) without proxying through the backend.
   * For videos, a `uploadThumbUrl` is also returned for the thumbnail.
   *
   * @param {string} objectName — GCS object path e.g. 'images/abc123.jpg'.
   * @param {string} fileType   — MIME type e.g. 'image/jpeg', 'video/mp4'.
   */
  async sessionurl(objectName, fileType) {
    return this.post('/session/generate-upload-url', {
      'objectName': objectName,
      'contentType': fileType
    })
  }

  /**
   * Delete one or more media objects from Google Cloud Storage.
   * Called when the fact-checker removes files from a post before saving.
   *
   * @param {Array<{url: string, thumbUrl: string|null}>} Objs — list of objects to delete.
   */
  async removeurl(Objs) {
    // console.log(Objs)
    return this.post('/session/remove-obj-file', {
      'Objs': Objs
    })
  }

  /**
   * Fetch the logged-in user's individual engagement scores for each
   * (timing × label) combination so the dashboard can draw a marker on the
   * distribution charts showing where the user sits relative to the population.
   *
   * @param {string}   usr_addr — user's backend address.
   * @param {string}   from_dt  — start date 'YYYY-MM-DD'.
   * @param {string}   to_dt    — end date   'YYYY-MM-DD'.
   * @param {string}   xpath    — region path prefix; '' = all regions.
   */
  async getUserDist(usr_addr, from_dt, to_dt, xpath) {
    return this.post('/session/user_dist', { usr_addr, from_dt, to_dt, xpath });
  }

  /**
   * Revert a released post back to BLANK so it re-enters fact-checking.
   * Only ADMIN role is authorised by the backend.
   *
   * @param {string} addr — '<index>/<id>' post address.
   */
  async revertPost(addr) {
    return this.post('/session/revert_post', { addr });
  }

  /**
   * Fetch channel eligibility stats for a given state.
   * Returns three monthly viewership percentages and the user's ADC rank.
   * Protected — Bearer token attached automatically by the axios interceptor.
   *
   * @param {string} state — GIS state name (e.g. 'TAMIL NADU').
   * @returns {{ allowed: boolean, m3_pct: number, m2_pct: number, m1_pct: number, adc_rank: number }}
   */
  async getChannelStats(state) {
    return this.post('/session/channel_stats', { state });
  }
}

// ---------------------------------------------------------------------------
// Singleton export — all page components import this one instance
// ---------------------------------------------------------------------------
const apiClient = new ApiClient();

export default apiClient;
export { TokenManager };

/**
 * validateAndRefreshToken — utility for components that need to check token
 * validity before performing a sensitive operation.
 *
 * Returns the current (possibly refreshed) token string, or null if the user
 * is not logged in or the refresh fails.
 */
export const validateAndRefreshToken = async () => {
  const userData = TokenManager.getUserData();

  if (!userData || !userData.token) {
    return null;
  }

  // If the stored token is still valid, return it immediately
  if (TokenManager.isTokenExpired(userData.token)) {
    try {
      // Token is expired — attempt a silent refresh
      const newToken = await apiClient.refreshToken();
      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      TokenManager.removeToken();
      return null;
    }
  }

  return userData.token;
};
